/**
 * Distance formatter — the units-subsystem primitive that converts a
 * canonical SI metre value to the operator's preferred distance unit
 * (ADR-033 §7).
 *
 * Meridian stores all spatial magnitudes in SI (metres); the UI converts at
 * render time per :type:`DistanceUnit` from the active Profile's
 * ``units.distance`` (REQ-UI-083). The three supported units:
 *
 *   - ``nautical_miles`` → ``m / 1852`` (the international nautical mile),
 *     suffix ``nm``.
 *   - ``kilometers`` → ``m / 1000``, suffix ``km``.
 *   - ``statute_miles`` → ``m / 1609.344`` (the international statute mile),
 *     suffix ``mi``.
 *
 * Output is fixed to one decimal place. A non-finite input (``NaN`` /
 * ``±Infinity`` — e.g. a missing or un-computable magnitude) renders as the
 * em-dash placeholder ``"—"`` rather than ``"NaN nm"``, so a readout never
 * surfaces a garbage value (operator-visibility, CLAUDE.md §8.1).
 *
 * NOT-YET-WIRED — lands in increment 6. This is the units-subsystem
 * primitive: it is exported + unit-tested here, but the first real render
 * site (the map scale control / detection-range / coverage-radius fields)
 * arrives in increment 6, which wires this in. It is intentionally not
 * attached to any render site yet — that is by design, not dead code. (In
 * particular, the dynamic-track km stepper is a km-denominated *config
 * input* bound to ``length_km`` and is deliberately left untouched — it is
 * not a render of a canonical-metre magnitude.)
 *
 * Cross-references:
 *   - REQ-UI-083 (distance-unit preference); ADR-033 §7.
 */
import type { DistanceUnit } from "@/auth/session-types";

/** Em-dash placeholder for a non-finite magnitude (matches the entity-detail
 *  section convention). */
const EM_DASH = "—";

/** Decimal places shown after conversion. */
const PRECISION = 1;

/**
 * Per-unit divisor (canonical metres → unit) plus the display suffix.
 * Divisors are the exact international definitions: 1 nm = 1852 m
 * (exact, by definition); 1 km = 1000 m; 1 statute mi = 1609.344 m (exact,
 * 1760 yards × 0.9144 m/yard).
 */
const UNIT_TABLE: Readonly<Record<DistanceUnit, { perMetre: number; suffix: string }>> = {
  nautical_miles: { perMetre: 1852, suffix: "nm" },
  kilometers: { perMetre: 1000, suffix: "km" },
  statute_miles: { perMetre: 1609.344, suffix: "mi" },
};

/**
 * Format a canonical metre magnitude in the operator's preferred unit.
 *
 * @param meters - The magnitude in SI metres (canonical storage unit).
 * @param unit - The target distance unit (from ``units.distance``).
 * @returns ``"<value> <suffix>"`` at one decimal place (e.g. ``"12.3 nm"``),
 *   or ``"—"`` when ``meters`` is not finite.
 */
export function formatDistance(meters: number, unit: DistanceUnit): string {
  if (!Number.isFinite(meters)) {
    return EM_DASH;
  }
  const { perMetre, suffix } = UNIT_TABLE[unit];
  return `${(meters / perMetre).toFixed(PRECISION)} ${suffix}`;
}
